import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { ChartConfiguration } from 'chart.js';
import { NGXLogger } from 'ngx-logger';
import { Subscription } from 'rxjs';
import FinancialDetailInfoViewModel from 'src/app/models/FinancialDetailInfoViewModel';
import { LinkedFileToObject } from 'src/app/models/LinkedFileToObject';
import { LinkedFilesToObjectWithId } from 'src/app/models/LinkedFilesToObjectWithId';
import { GetAllFilesLinkedToObject } from 'src/app/shared/global-files/global-files.actions';
import { GlobalFilesState } from 'src/app/shared/global-files/global-files.state';
import { externalTooltipHandler } from 'src/app/shared/utils/customtooltip-utils';

import { BudgetCalculationService } from '../../budget/budget-calculation.service';
import { FetchEmployeesWithBudgets } from '../../budget/budget.actions';
import { BudgetState } from '../../budget/budget.state';
import { MONTHS } from '../../models/MONTHS';
import Project from '../../models/Project';
import { SettingsState } from '../../settings/settings.state';
import { CalendarClickEventData, LegendPosition } from '../../shared/charts/default-line-chart/default-line-chart.component';
import { DateNavigationOptions, DateNavigationType, SelectedDateClass } from '../../shared/date-navigation/date-navigation.component';
import { roundNumber } from '../../shared/utils/number-utils';
import { capitalizeFirstLetter } from '../../shared/utils/string-utils';
import { ChartInformationModalComponent, ChartInformationObjectType } from '../chart-information-modal/chart-information-modal.component';
import { DATASETS } from '../employee-graph/employee-graph.component';
import { FetchCostsForProject, FetchIncomeForProject } from '../financial.actions';
import { FinancialService } from '../financial.service';
import { FinancialState } from '../financial.state';

@Component({
    selector: 'app-project-graph',
    templateUrl: './project-graph.component.html',
    styleUrls: ['./project-graph.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ProjectGraphComponent implements OnInit, OnDestroy {
    @Input() projectId: string;
    @Input() project: Project;

    data: any[] = undefined;
    labels: string[] = [];
    xLabels: string[];
    legendPosition: LegendPosition = LegendPosition.BOTTOM;
    options: ChartConfiguration['options'];
    incomeForProject: FinancialDetailInfoViewModel;
    costsForProject: FinancialDetailInfoViewModel;
    linkedFilesToObject?: LinkedFilesToObjectWithId;
    purchaseCostsByMonth: number[] = new Array(12).fill(0);
    laborCostsByMonth: number[] = new Array(12).fill(0);

    isLoading = false;
    selectedDate: Date = new Date();
    dateNavigationOptions: DateNavigationOptions;

    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' = 'asc';

    private subscriptions = new Subscription();

    constructor(
        public store: Store,
        private logger: NGXLogger,
        private modalService: NgbModal,
        private financialService: FinancialService,
        private budgetCalc: BudgetCalculationService,
    ) {}

    ngOnInit(): void {
        this.dateNavigationOptions = {
            dateNavigationType: DateNavigationType.YEAR,
            selectedDate: this.selectedDate,
            selectedDateClass: SelectedDateClass.SMALL,
        };

        this.fetchData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    seedLabels() {
        return Object.values(MONTHS).map((label) => capitalizeFirstLetter(label.short));
    }

    updateSelectedDate(): void {
        this.isLoading = true;
        let isIncomeLoading = true;
        let isCostsLoading = true;
        this.subscriptions.add(
            this.store.dispatch(new FetchIncomeForProject(this.projectId, this.dateNavigationOptions.selectedDate.getFullYear())).subscribe({
                next: () => {
                    isIncomeLoading = false;
                    if (!isCostsLoading) this.isLoading = false;
                },
            }),
        );
        this.subscriptions.add(
            this.store.dispatch(new FetchCostsForProject(this.projectId, this.dateNavigationOptions.selectedDate.getFullYear())).subscribe({
                next: () => {
                    isCostsLoading = false;
                    if (!isIncomeLoading) this.isLoading = false;
                },
            }),
        );

        // Recompute purchase and labor costs per month for new year
        this.recomputePurchaseCostsByMonth();
        this.recomputeLaborCostsByMonth();
        if (this.incomeForProject?.financialData?.length === 12) {
            this.createChart();
        }
    }

    calculateTotalMarginForSelectedYearInPct(): number {
        const toSafe = (v: any) => {
            const n = Number(v || 0);
            return isFinite(n) && !isNaN(n) ? n : 0;
        };
        const monthsToInclude = this.getMonthsToInclude();
        const totalIncome: number = this.incomeForProject.financialData
            .slice(0, monthsToInclude)
            .reduce((total: number, financialData: number) => total + toSafe(financialData), 0);

        const totalCost: number = this.getAugmentedCosts()
            .slice(0, monthsToInclude)
            .reduce((total: number, financialData: number) => total + toSafe(financialData), 0);

        const margin = totalIncome - totalCost;
        if (margin === 0) return 0;
        if (totalIncome > 0) return roundNumber(margin / totalIncome, 4);
        if (totalCost > 0) return roundNumber(margin / totalCost, 4);
        return margin < 0 ? -1 : 0;
    }

    calculateTotalCostForSelectedYear(): number {
        const toSafe = (v: any) => {
            const n = Number(v || 0);
            return isFinite(n) && !isNaN(n) ? n : 0;
        };
        return this.getAugmentedCosts()
            .slice(0, this.getMonthsToInclude())
            .reduce((total: number, financialData: number) => total + toSafe(financialData), 0);
    }

    calculateTotalIncomeForSelectedYear(): number {
        const toSafe = (v: any) => {
            const n = Number(v || 0);
            return isFinite(n) && !isNaN(n) ? n : 0;
        };
        return this.incomeForProject.financialData
            .slice(0, this.getMonthsToInclude())
            .reduce((total: number, financialData: number) => total + toSafe(financialData), 0);
    }

    calculateTotalMarginForSelectedYear(): number {
        const toSafe = (v: any) => {
            const n = Number(v || 0);
            return isFinite(n) && !isNaN(n) ? n : 0;
        };
        const monthsToInclude = this.getMonthsToInclude();
        const totalIncome: number = this.incomeForProject.financialData
            .slice(0, monthsToInclude)
            .reduce((total: number, financialData: number) => total + toSafe(financialData), 0);

        const totalCost: number = this.getAugmentedCosts()
            .slice(0, monthsToInclude)
            .reduce((total: number, financialData: number) => total + toSafe(financialData), 0);

        return totalIncome - totalCost;
    }

    /**
     * Number of months to include in totals/chart, mirroring the employee-graph logic.
     *  - selected year != current year                                          -> 12
     *  - current year + setting "Include current month" ON                      -> 1..currentMonth
     *  - current year + setting OFF (default)                                   -> 1..currentMonth-1
     */
    private getMonthsToInclude(): number {
        const now = new Date();
        const selectedYear = this.dateNavigationOptions?.selectedDate?.getFullYear?.() || now.getFullYear();
        if (selectedYear !== now.getFullYear()) {
            return 12;
        }
        const includeCurrent = this.store.selectSnapshot(SettingsState.FinancialsIncludeCurrentMonth);
        const currentMonth = now.getMonth() + 1; // 1-based
        return includeCurrent ? currentMonth : Math.max(0, currentMonth - 1);
    }

    displayInfo($event: CalendarClickEventData) {
        const modalRef = this.modalService.open(ChartInformationModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });
        modalRef.componentInstance.month = $event.month;
        modalRef.componentInstance.year = this.dateNavigationOptions.selectedDate.getFullYear();
        modalRef.componentInstance.dataset = DATASETS.findDataset($event.datasetIndex);
        modalRef.componentInstance.objectId = this.projectId;
        modalRef.componentInstance.title = `${this.project.title}`;
        modalRef.componentInstance.chartInformationObjectType = ChartInformationObjectType.PROJECT;
    }

    private fetchData() {
        this.xLabels = this.seedLabels();

        this.subscriptions.add(
            this.store.select(FinancialState.incomeForProject(this.projectId)).subscribe((income) => {
                if (income?.financialData.length !== 12 || income.detailId !== this.projectId) {
                    this.store.dispatch(new FetchIncomeForProject(this.projectId));
                    return;
                }

                // Normalize financial data to numbers to avoid string concatenation issues
                this.incomeForProject = {
                    ...income,
                    financialData: (income.financialData || []).map((v: any) => Number(v || 0)),
                } as FinancialDetailInfoViewModel;

                if (this.costsForProject?.financialData.length === 12) this.createChart();
            }),
        );

        this.subscriptions.add(
            this.store.select(FinancialState.costsForProject(this.projectId)).subscribe((costs) => {
                if (costs?.financialData.length !== 12 || costs.detailId !== this.projectId) {
                    this.store.dispatch(new FetchCostsForProject(this.projectId));
                    return;
                }

                // Normalize financial data to numbers to avoid string concatenation issues
                this.costsForProject = {
                    ...costs,
                    financialData: (costs.financialData || []).map((v: any) => Number(v || 0)),
                } as FinancialDetailInfoViewModel;

                if (this.incomeForProject?.financialData.length === 12) this.createChart();
            }),
        );

        // Fetch linked files (invoices) for this project to include purchase invoices in costs
        this.subscriptions.add(this.store.dispatch(new GetAllFilesLinkedToObject(this.projectId)).subscribe());
        this.subscriptions.add(
            this.store.select(GlobalFilesState.LinkedFilesToObjectWithId).subscribe((linked) => {
                this.linkedFilesToObject = linked;
                this.recomputePurchaseCostsByMonth();
                this.recomputeLaborCostsByMonth();
                if (this.incomeForProject?.financialData?.length === 12) {
                    this.createChart();
                }
            }),
        );
    }

    private recomputePurchaseCostsByMonth() {
        const toSafe = (v: any) => {
            const n = Number(v || 0);
            return isFinite(n) && !isNaN(n) ? n : 0;
        };
        const year = this.dateNavigationOptions?.selectedDate?.getFullYear?.() ?? new Date().getFullYear();
        const files: LinkedFileToObject[] = this.linkedFilesToObject?.linkedFiles || [];

        // Use EXACT same filter logic as linked-invoices-to-object component
        const startDate = new Date(year, 0, 1); // Jan 1
        const endDate = new Date(year, 11, 31, 23, 59, 59); // Dec 31 end of day

        const purchases = files.filter((data) => {
            // EXACT copy of filterPredicate from linked-invoices-to-object.component.ts
            // Exclude deleted/archived invoices based on available flags/metadata
            const type = (data.invoiceType || '').toLowerCase();
            const desc = (data.description || '').toLowerCase();
            const title = (data.title || '').toLowerCase();
            const isArchivedOrDeleted =
                type.includes('archived') ||
                type.includes('deleted') ||
                desc.includes('archived') ||
                desc.includes('deleted') ||
                title.includes('archived') ||
                title.includes('deleted');

            return (
                data.fileType?.toLowerCase() === 'yukidocument' &&
                data.folderId === 1 && // Purchase folder
                data.invoiceDate &&
                new Date(data.invoiceDate) < endDate &&
                new Date(data.invoiceDate) > startDate &&
                !isArchivedOrDeleted
            );
        });

        this.logger.debug('[ProjectGraph] all linked files', {
            totalFiles: files.length,
            purchaseFiles: files.filter((f) => f.folderId === 1).length,
            filteredPurchases: purchases.length,
            year,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
        });

        const byMonth = new Array(12).fill(0);
        purchases.forEach((p) => {
            const d = new Date(p.invoiceDate);
            const m = d.getMonth();
            // Use absolute value so costs are positive in charts/aggregates
            byMonth[m] += Math.abs(toSafe(p.price));
        });

        this.purchaseCostsByMonth = byMonth.map((v) => toSafe(v));
        const totalPurchase = this.purchaseCostsByMonth.reduce((a, b) => a + b, 0);

        this.logger.debug('[ProjectGraph] purchase costs final', {
            byMonth: this.purchaseCostsByMonth,
            totalPurchase,
            count: purchases.length,
            purchases: purchases.map((p) => ({
                title: p.title,
                price: p.price,
                abs: Math.abs(toSafe(p.price)),
                date: p.invoiceDate,
            })),
        });
    }

    private getAugmentedCosts(): number[] {
        // Canonical source: backend FinancialService.computeCanonicalProjectDeliveryCosts.
        // Keep this graph on the shared backend calculation so project detail, financial
        // project reports, target reports, and target mails cannot drift again.
        return (this.costsForProject?.financialData || new Array(12).fill(0)).map((value: any) => {
            const n = Number(value || 0);
            return isFinite(n) && !isNaN(n) ? n : 0;
        });
    }

    private recomputeLaborCostsByMonth() {
        const toSafe = (v: any) => {
            const n = Number(v || 0);
            return isFinite(n) && !isNaN(n) ? n : 0;
        };
        const year = this.dateNavigationOptions?.selectedDate?.getFullYear?.() ?? new Date().getFullYear();
        this.financialService.getTimesheetsForProject(this.projectId, year, null).subscribe({
            next: (timesheets: any[]) => {
                this.logger.debug('[ProjectGraph] timesheets fetched', { count: timesheets?.length || 0, year });

                // If no timesheets, labor costs are zero
                if (!timesheets || timesheets.length === 0) {
                    this.laborCostsByMonth = new Array(12).fill(0);
                    this.logger.debug('[ProjectGraph] no timesheets; labor costs = 0');
                    if (this.incomeForProject?.financialData?.length === 12) {
                        this.createChart();
                    }
                    return;
                }

                const employeeIds: string[] = Array.from(new Set((timesheets || []).map((ts: any) => ts?.employee?.uuid).filter((id: string) => !!id)));
                if (employeeIds.length === 0) {
                    this.laborCostsByMonth = new Array(12).fill(0);
                    this.logger.debug('[ProjectGraph] no employees in timesheets; labor costs = 0');
                    if (this.incomeForProject?.financialData?.length === 12) {
                        this.createChart();
                    }
                    return;
                }

                // Ensure BudgetState is loaded, then calculate
                const employeesWithBudgets = this.store.selectSnapshot(BudgetState.EmployeesWithBudgets);
                if (!employeesWithBudgets) {
                    this.store.dispatch(new FetchEmployeesWithBudgets()).subscribe(() => {
                        this.aggregateLaborCostsUsingBudgets(timesheets, year);
                    });
                } else {
                    this.aggregateLaborCostsUsingBudgets(timesheets, year);
                }
            },
            error: () => {
                this.laborCostsByMonth = new Array(12).fill(0);
                this.logger.error('[ProjectGraph] timesheets fetch error; labor costs = 0');
            },
        });
    }

    private aggregateLaborCostsUsingBudgets(timesheets: any[], year: number) {
        const toSafe = (v: any) => {
            const n = Number(v || 0);
            return isFinite(n) && !isNaN(n) ? n : 0;
        };
        const byMonth = new Array(12).fill(0);
        const employeesWithBudgets = this.store.selectSnapshot(BudgetState.EmployeesWithBudgets) || [];

        // Build per employee-per-month aggregates: hours worked on project
        const perEmpMonth: Record<string, { [month: number]: number }> = {}; // empId -> month -> hours

        (timesheets || []).forEach((ts: any) => {
            const empId = ts?.employee?.uuid;
            if (!empId) return;
            const tsDate = new Date(ts?.expirationDate);
            const tsYear = tsDate.getFullYear();
            const tsMonth = tsDate.getMonth();
            if (tsYear !== year) return;

            if (!perEmpMonth[empId]) perEmpMonth[empId] = {};
            if (!perEmpMonth[empId][tsMonth]) perEmpMonth[empId][tsMonth] = 0;

            const timeslots = ts?.timeslots || [];
            timeslots.forEach((slot: any) => {
                if (slot?.project?.uuid === this.projectId) {
                    const minutes = toSafe(slot?.minutes || 0);
                    perEmpMonth[empId][tsMonth] += minutes / 60; // convert to hours
                }
            });
        });

        // For each employee-month, use BudgetCalculationService to compute cost
        Object.keys(perEmpMonth).forEach((empId) => {
            const match = employeesWithBudgets.find((e: any) => e.uuid === empId);
            if (!match) return;
            const budgets = match.budgets || [];
            const workPercentage = match.workPercentage; // e.g., 100, 80, 60

            Object.keys(perEmpMonth[empId]).forEach((mStr) => {
                const m = Number(mStr);
                const hoursWorked = perEmpMonth[empId][m];
                if (hoursWorked <= 0) return;

                // Use middle of month for budget calculation
                const targetDate = new Date(year, m, 15);
                const monthlyTotal = this.budgetCalc.computeMonthlyBudgetTotal(budgets, targetDate);
                const { projectCost } = this.budgetCalc.computeProjectCost(monthlyTotal, hoursWorked, workPercentage);
                byMonth[m] += projectCost;
            });
        });

        this.laborCostsByMonth = byMonth.map((v) => toSafe(v));
        const totalLabor = this.laborCostsByMonth.reduce((a, b) => a + toSafe(b), 0);
        this.logger.debug('[ProjectGraph] labor costs recomputed using BudgetCalc', { byMonth: this.laborCostsByMonth, totalLabor });
        if (this.incomeForProject?.financialData?.length === 12) {
            this.createChart();
        }
    }

    private createChart() {
        const margin: number[] = [];
        const marginPercentage: number[] = [];
        const costsAugmented = this.getAugmentedCosts();
        const income = this.incomeForProject.financialData;
        const monthsToInclude = this.getMonthsToInclude();
        for (let i = 0; i < costsAugmented.length; i++) {
            // Honor the "Include current month in financial overviews" setting:
            // months from monthsToInclude onward are forced to 0 in the chart so they don't
            // show partial cost/income lines that contradict the setting.
            const inc = i < monthsToInclude ? Number(income[i] || 0) : 0;
            const cost = i < monthsToInclude ? Number(costsAugmented[i] || 0) : 0;
            const m = inc - cost;
            margin.push(m);
            marginPercentage.push(inc !== 0 ? m / inc : cost > 0 ? -1 : 0);
        }

        const cappedCosts = costsAugmented.map((v, i) => (i < monthsToInclude ? Number(v || 0) : 0));
        const cappedIncome = (this.incomeForProject.financialData || []).map((v, i) => (i < monthsToInclude ? Number(v || 0) : 0));

        // Show full year to match Linked Invoices yearly totals
        const lastMonth: number = 12;

        this.xLabels = this.seedLabels();
        for (let i = 0; i < this.xLabels.length; i++) {
            if (!isNaN(marginPercentage[i]) && Number.isFinite(marginPercentage[i])) {
                this.xLabels[i] += `: ${roundNumber(marginPercentage[i] * 100)}%`;
            }
        }

        this.data = [cappedCosts.slice(0, lastMonth), cappedIncome.slice(0, lastMonth), margin.slice(0, lastMonth)];
        this.labels = ['Cost', 'Income', 'Margin'];

        this.options = {
            plugins: {
                tooltip: {
                    enabled: false,
                    position: 'nearest',
                    external: externalTooltipHandler,
                },
            },
        };
    }

    // Call this method from the template when a column header is clicked
    onSort(column: string) {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.sortData();
    }

    sortData() {
        if (!this.data || !this.sortColumn) return;
        // Assume data is an array of objects with keys matching column names
        this.data.sort((a, b) => {
            let aValue = a[this.sortColumn];
            let bValue = b[this.sortColumn];
            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }
}
